import { SIGNED_OUT_LOCALE_STORAGE_KEY } from '@brightdrop/localization-client';
import {
  isSupportedLocale,
  type SupportedLocale,
} from '@brightdrop/localization-core';

// Storage access throws when the browser blocks site data. Each helper treats
// that as nothing stored, so the picker and startup still work for the tab.

/**
 * Reads the locale a visitor picked before they had a profile.
 * @returns The stored locale, or `undefined` when none is stored, it is not an offered locale, or storage is blocked
 */
export const getStoredLocale = (): SupportedLocale | undefined => {
  try {
    const stored =
      localStorage.getItem(SIGNED_OUT_LOCALE_STORAGE_KEY) ?? undefined;
    return isSupportedLocale(stored) ? stored : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Stores the locale a visitor picked before they had a profile.
 * @param locale - The picked locale
 */
export const setStoredLocale = (locale: SupportedLocale): void => {
  try {
    localStorage.setItem(SIGNED_OUT_LOCALE_STORAGE_KEY, locale);
  } catch {
    // The choice then lasts only as long as the tab's in-memory state.
  }
};

/**
 * Forgets the signed-out visitor's choice. Once signed in, the profile language
 * decides, so a choice made before sign-in must not outlive the session.
 */
export const clearStoredLocale = (): void => {
  try {
    localStorage.removeItem(SIGNED_OUT_LOCALE_STORAGE_KEY);
  } catch {
    // Nothing could have been stored, so there is nothing to forget.
  }
};
